import { Clip, Subtitle } from '@schema/types';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

export function sigfigs(num: number, figs: number): number {
  const mul = 10 ** figs;
  return Math.round(num * mul) / mul;
}

export function clipsHash(clips: Clip[]): number {
  let result = 13;

  for (const clip of clips) {
    result *= (clip.startTime || 1) * clip.endTime;
  }

  return result;
}

export function findClipIndexAtTime(clips: Clip[], time: number): number {
  time = sigfigs(time, 3); // eslint-disable-line

  for (let i = 0; i < clips.length; i++) {
    const clip = clips[i];

    if (sigfigs(clip.startTime, 3) <= time && sigfigs(clip.endTime, 3) >= time) {
      return i;
    }
  }

  return -1;
}

// From: https://davidwalsh.name/javascript-debounce-function
// eslint-disable-next-line @typescript-eslint/ban-types
export function debounce(func: Function, wait: number, immediate = false): () => void {
  let timeout: NodeJS.Timeout | null = null;

  // eslint-disable-next-line
  return function (this: typeof func): void {
    const context = this; // eslint-disable-line
    const args = arguments; // eslint-disable-line

    function later(): void {
      timeout = null;
      if (!immediate) func.apply(context, args);
    }

    const callNow = immediate && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}
// eslint-disable-next-line @typescript-eslint/ban-types
export function throttle(f: Function, timeBetweenCalls: number): (() => void) | undefined {
  if (!f) {
    return undefined;
  }

  let lastCall = 0;

  return (...args): void => {
    const now = performance.now();

    if (now - lastCall > timeBetweenCalls) {
      lastCall = now;
      f(...args);
    }
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(Math.min(value, max), min);
}

/**
 * convert time string ('HH:MM:SS'/'MM:SS') to milliseconds
 * @param time
 */
export function getMilliseconds(time: string): number {
  if (!time) return 0;
  const values = time.split(':');
  if (values.length < 2 || values.length > 3) return 0;
  if (values.length === 2) {
    return (60 * Number(values[0]) + Number(values[1])) * 1000;
  }
  return (Number(values[0]) * 60 * 60 + Number(values[1]) * 60 + Number(values[2])) * 1000;
}

export function formatTime(time: number, keepZeroHours = false): string {
  const hours = Math.floor((time / (1000 * 60 * 60)) % 24)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((time / (1000 * 60)) % 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor((time / 1000) % 60)
    .toString()
    .padStart(2, '0');

  return hours !== '00' || keepZeroHours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
}

export function formatMilliseconds(time: number, decimalPlaces: 1 | 2 | 3): string {
  return `${time % 1000}`.padStart(3, '0').substring(0, decimalPlaces);
}

const region = publicRuntimeConfig.CVENT_REGION;
export const isProduction = ['p', 'int', 'ct'].some(x => region.startsWith(x));
export const isWhitelistedRegion = ['dev', 'sg', 'ci'].some(x => region.startsWith(x));

export const findPrevSubtitleByTime = (subtitles: Subtitle[], time: number): Subtitle => {
  if (!subtitles.length || subtitles.length === 1) {
    return null;
  }
  return subtitles.reduce((a, b) => {
    return Math.abs(b.endTime - time) < Math.abs(a.endTime - time) && b.endTime < time ? b : a;
  });
};

export const findNextSubtitleByTime = (subtitles: Subtitle[], time: number): Subtitle => {
  return subtitles.reduce((a, b) => {
    return Math.abs(b.startTime - time) < Math.abs(a.startTime - time) && b.startTime > time ? b : a;
  });
};

export const fitSubtitle = (value, min, max, duration): { start: number; end: number } => {
  const diff = max - min;
  if (diff > duration) {
    const start = Math.min(value, max - duration);
    return { start: Math.min(value, max - duration), end: start + duration };
  }
  return { start: min, end: max };
};

export const findSubtitlesAround = (
  subtitles: Subtitle[],
  time: number
): { prevSubtitle: Subtitle; nextSubtitle: Subtitle } => {
  const nextSubtitle = subtitles.find(subtitle => subtitle.startTime > time);
  const prevSubtitle$ = findPrevSubtitleByTime(subtitles, time);
  const prevSubtitle = prevSubtitle$ && prevSubtitle$.id !== nextSubtitle?.id ? prevSubtitle$ : null;

  return { prevSubtitle, nextSubtitle };
};

export const inRange = (value: number, min: number, max: number): boolean => {
  return value >= min && value <= max;
};

/**
 * Adds an incremental number to a label or filename.  For example,
 * "My Title" becomes "My Title (1)"
 * "My Title (1)" becomes "My Title (2)"
 * "my_file.txt" becomes "my_file (1).txt"
 *
 * @param value - the string to add/edit incremental value of
 */
export const incrementString = (value: string): string => {
  let incrementedValue = 1;
  const maxCharacterLimit = 300;
  const matcher = /\((\d+)\)$/;
  let cleansedValue = value.trim();
  let extension = '';
  const extensionIndex = cleansedValue.lastIndexOf('.');
  if (extensionIndex > 0 && extensionIndex < cleansedValue.length - 1) {
    // only consider extension if . isn't first or last character
    extension = cleansedValue.substring(extensionIndex);
    cleansedValue = cleansedValue.substring(0, extensionIndex).trim();
  }
  const match = cleansedValue.match(matcher);
  if (match && match[1]) {
    incrementedValue = parseInt(match[1], 10) + 1;
  }
  const incrementPostFix = ` (${incrementedValue})`;
  cleansedValue = cleansedValue.trim().replace(matcher, '').trim();
  cleansedValue = cleansedValue.substring(
    0,
    clamp(cleansedValue.length, 0, maxCharacterLimit - incrementPostFix.length - extension.length)
  );
  return `${cleansedValue}${incrementPostFix}${extension}`;
};

export const findClosestInArray = (value: number, arr: number[]): number => {
  return arr.reduce((r, i) => (Math.abs(value - i) > Math.abs(value - r) ? r : i));
};
